/**
 * Numbers shared between the composition and its sequences.
 *
 * They live here rather than in TikTokVideoVanilla because importing that module pulls in mapbox-gl
 * and its `accessToken` assignment, which breaks any test that mocks mapbox away -- PathSequence's
 * suite failed to load for exactly that reason once its sequences started importing constants.
 */

/** Under this a leg is "local" -- a crawl, an island loop, a day out -- and the camera stays low. */
export const CITY_LEG_KM = 40;
/** ~zoom 15.1 at 1080x1920. The ceiling only binds on the longest local legs. */
export const CITY_MAX_ALTITUDE = 12000;
/** How many photos fan out at a stop, when photo cards are switched on. */
export const CARDS_PER_STOP = 3;
/** Size of the shared card image pool. */
export const CARD_POOL = 6;

/**
 * How much ground a STOP shows -- the intro's landing, the film's end, and every arrival in between.
 *
 * A stop used to inherit the height of the shorter leg touching it, which is the height the ROUTE
 * needs, not the place. On a coach trip that meant arriving at Phantasialand looking at 69 km of
 * ground, where the park is half a kilometre wide: a dot. Ten kilometres shows a town.
 *
 * It costs nothing in length. Leg durations come from distance, and on that trip the legs already
 * run 20.4 s where the zoom this asks for needs 12.8 s at the 3-levels-per-second ceiling -- so the
 * climb out of each stop fits in slack that was already there.
 */
export const STOP_VIEW_KM = 10;

/** Half the vertical field of view of Mapbox's camera, as a tangent. The lens the whole film uses. */
export const FOV_TAN_HALF = Math.tan((36.87 * Math.PI) / 360);

/**
 * The most ground the camera may cross in a single frame, as a fraction of what it can see. Above
 * this it is travelling faster than the map can be drawn: measured leaving Bottle Beach at 16,535 m,
 * the camera moved 15 km per frame through a view about 4 km wide -- four screenfuls a frame -- and
 * the map simply came out blank.
 */
export const MOTION_FRACTION = 0.12;

/**
 * Below this, a leg is short enough that the interesting thing is the ground rather than the route.
 * Framing every leg so it fits entirely on screen means an island's 630 m of relief sits under a
 * tenth of the view and reads as flat -- correct framing, dull picture.
 */
export const SHORT_LEG_KM = 20;

/**
 * How far below its own framing height a short leg is flown. At 0.55 the route can run past the
 * edges of the frame; that is the trade being made deliberately, in exchange for feeling the terrain.
 */
export const SHORT_LEG_DROP = 0.35;

/**
 * The lowest the camera is ever put, in metres.
 *
 * It was 1,000 m, written twice, and it bound on every city stop: a bar hop or a dinner walk framed
 * at 1.35 km of ground, which is a neighbourhood rather than a street. Mapbox draws buildings as
 * volumes from about zoom 16 and crisply by 17, which is roughly here. Below this they start to
 * clip, and a walking leg is already well outside a frame this narrow -- 560 m of ground against a
 * kilometre of route. That is the trade: the place is legible, the whole line is not.
 */
export const MIN_ALTITUDE = 283;
